import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { whatsappNumber } from '@goyone/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { Menu, type MenuAction } from '../../components/Menu';
import { Modal } from '../../components/ui';
import { OwnerPickerModal } from '../../components/People';
import { useToast } from '../../components/Toast';
import { DeleteDialog } from '../../components/DeleteDialog';
import { ActivityComposer } from './ActivityComposer';

/**
 * الإجراءات السريعة على جهة اتصال.
 *
 * واتساب هنا إحالةٌ إلى التطبيق عبر `wa.me` لا تكاملٌ مع القناة: لا رسائل
 * تُرسَل من النظام ولا محادثات تُقرأ فيه. ويُخفى الزرّ إن لم يكن للرقم مفتاح
 * دولة — رابطٌ بلا مفتاح يفتح محادثة مع رقم في بلد آخر.
 */

type Mode = 'owner' | 'activity' | 'delete' | null;

export interface QuickContact {
  id: string;
  name: string;
  accountId: string;
  ownerId?: string | null;
  /** المسؤول الفعلي: الخاص بها أو الموروث من العميل. */
  effectiveOwnerId?: string | null;
  email?: string | null;
  phone?: string | null;
}

export function ContactQuickActions({
  contact,
  onEdit,
  onChanged,
  className = 'btn btn-sm btn-icon btn-ghost',
}: {
  contact: QuickContact;
  onEdit?: () => void;
  onChanged: () => void;
  className?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>(null);

  const wa = whatsappNumber(contact.phone);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/crm/contacts/${contact.id}`, { method: 'PATCH', body }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries();
      setMode(null);
      toast(
        variables.ownerId
          ? `صار لـ«${contact.name}» مسؤول خاص`
          : `عادت «${contact.name}» إلى مسؤول عميلها`,
      );
      onChanged();
    },
  });

  const actions: MenuAction[] = [
    {
      key: 'owner',
      label: 'تغيير المسؤول',
      icon: 'users',
      onSelect: () => setMode('owner'),
    },
    {
      key: 'call',
      label: 'اتصال',
      icon: 'phone',
      disabled: !contact.phone,
      title: contact.phone ?? 'لا رقم مسجَّل',
      onSelect: () => {
        navigate(`/callcenter?dial=${encodeURIComponent(contact.phone ?? '')}`);
      },
    },
    {
      key: 'wa',
      label: 'محادثة واتساب',
      icon: 'chat',
      disabled: !wa,
      title: wa ? undefined : 'يلزم رقم بمفتاح دولة',
      onSelect: () => {
        window.open(`https://wa.me/${wa}`, '_blank', 'noopener');
      },
    },
    {
      key: 'mail',
      label: 'إرسال بريد',
      icon: 'mail',
      disabled: !contact.email,
      title: contact.email ?? 'لا بريد مسجَّل',
      onSelect: () => {
        window.location.href = `mailto:${contact.email}`;
      },
    },
    {
      key: 'activity',
      label: 'تسجيل نشاط',
      icon: 'clipboard',
      separated: true,
      onSelect: () => setMode('activity'),
    },
    {
      key: 'account',
      label: 'فتح ملف العميل',
      icon: 'building',
      onSelect: () => navigate(`/crm/accounts/${contact.accountId}`),
    },
  ];

  if (onEdit) {
    actions.push({
      key: 'edit',
      label: 'تعديل البيانات',
      icon: 'edit',
      separated: true,
      onSelect: onEdit,
    });
  }

  if (atLeast('MANAGER')) {
    actions.push({
      key: 'delete',
      label: 'حذف',
      icon: 'trash',
      danger: true,
      onSelect: () => setMode('delete'),
    });
  }

  return (
    <>
      <Menu actions={actions} label={`إجراءات ${contact.name}`} className={className} />

      {mode === 'owner' && (
        <OwnerPickerModal
          currentId={contact.ownerId ?? null}
          title={`مسؤول ${contact.name}`}
          pending={patch.isPending}
          error={patch.error}
          onPick={(userId) => patch.mutate({ ownerId: userId })}
          onClose={() => setMode(null)}
        />
      )}

      {mode === 'activity' && (
        <Modal title={`نشاط مع ${contact.name}`} onClose={() => setMode(null)}>
          <ActivityComposer
            link={{ contactId: contact.id, accountId: contact.accountId }}
            onDone={() => {
              void queryClient.invalidateQueries();
              setMode(null);
              onChanged();
            }}
          />
        </Modal>
      )}

      {mode === 'delete' && (
        <DeleteDialog
          entity="contact"
          id={contact.id}
          endpoint={`/crm/contacts/${contact.id}`}
          onClose={() => setMode(null)}
          onDeleted={() => {
            setMode(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}
